import { Badge, Flex, Text } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';
import { LuMenu } from 'react-icons/lu';
import type { ShellNavItem } from './types';

/* Fixed bottom nav, mobile-only (`base`..`lg`). ResponsiveShell only mounts this when it has
   >=2 tab items to show — see the `showTabBar` check there — so there's no "one lonely tab"
   state to design for. */

export const TAB_BAR_HEIGHT = '56px';
/** Below the two full-viewport takeovers already in the app — MapPage's fullscreen mode
    (zIndex 1500, OrdersMap.tsx) and ChatPage's image-zoom overlay (zIndex 1400) — so both
    correctly cover the bar with no change needed on their side. */
export const TAB_BAR_Z_INDEX = 1200;

interface ShellTabBarProps {
  items: ShellNavItem[];
  menuLabel: string;
  onOpenMenu: () => void;
}

export function ShellTabBar({ items, menuLabel, onOpenMenu }: ShellTabBarProps) {
  return (
    <Flex
      as="nav"
      position="fixed"
      bottom="0"
      insetInline="0"
      pb="env(safe-area-inset-bottom, 0px)"
      bg="bg.panel"
      borderTopWidth="1px"
      borderColor="border"
      zIndex={TAB_BAR_Z_INDEX}
      display={{ base: 'flex', lg: 'none' }}
    >
      {items.map((item) => {
        const Icon = item.icon;
        const badge = item.badge ?? 0;
        return (
          <Link
            key={item.key}
            to={item.to}
            activeOptions={{ exact: item.exact }}
            style={{ textDecoration: 'none', flex: 1 }}
          >
            {({ isActive }) => (
              <Flex
                direction="column"
                align="center"
                justify="center"
                h={TAB_BAR_HEIGHT}
                gap="0.5"
                position="relative"
                color={isActive ? 'colorPalette.fg' : 'fg.muted'}
              >
                <Icon size={20} />
                <Text fontSize="0.65rem" lineHeight="1">
                  {item.label}
                </Text>
                {badge > 0 && (
                  <Badge
                    position="absolute"
                    top="2px"
                    right="calc(50% - 22px)"
                    colorPalette="red"
                    borderRadius="full"
                    fontSize="0.55rem"
                    minW="14px"
                    textAlign="center"
                    px="1"
                  >
                    {badge > 99 ? '99+' : badge}
                  </Badge>
                )}
              </Flex>
            )}
          </Link>
        );
      })}
      <Flex
        as="button"
        aria-label={menuLabel}
        onClick={onOpenMenu}
        direction="column"
        align="center"
        justify="center"
        flex="1"
        h={TAB_BAR_HEIGHT}
        gap="0.5"
        color="fg.muted"
      >
        <LuMenu size={20} />
        <Text fontSize="0.65rem" lineHeight="1">
          {menuLabel}
        </Text>
      </Flex>
    </Flex>
  );
}
